import type { TableColumnStaticProps } from '@/types/tableTypes';
import { MIN_COLUMN_WIDTH_PX } from '@/components/DataTable/minColumnWidth';
import { customListScheduleTableColumns } from '@/components/FilterBuilder/filterControlRegistry';

/**
 * Per-entity custom (non-model-derived) columns for the schema-driven search table.
 *
 * The search table builds its columns from models.json, so anything that isn't a model
 * field — like the Play column — has to be injected as an additional column keyed by the
 * selected entity id. Add entries here to give a specific entity extra columns.
 */

const PLAY_COLUMN: TableColumnStaticProps = {
  id: 'play',
  key: 'play',
  header: 'Play',
  dataType: 'display',
  field: 'play',
  isFilled: false,
  isProhibited: false,
  isUnique: false,
  isSortable: false,
  isFilterable: false,
  // Single fixed width — the Play button is a fixed-size icon, so the column never resizes.
  fixedWidth: MIN_COLUMN_WIDTH_PX,
};

/**
 * Location Zone Player "Currently Playing" column: the live now-playing block
 * (cover + track / playlist / volume). A custom column so Column Settings can
 * toggle it; it renders as a rich block on the cards and in the table cell.
 * On by default in the card layout (see cardDefaultColumnIdsFor), not fixed.
 */
const CURRENTLY_PLAYING_COLUMN: TableColumnStaticProps = {
  id: 'currently_playing',
  key: 'currently_playing',
  header: 'Currently Playing',
  dataType: 'display',
  field: 'currently_playing',
  isFilled: false,
  isProhibited: false,
  isUnique: false,
  isSortable: false,
  isFilterable: false,
  cardOnly: true,
};

/** Entity ids whose search table gets a leading Play column. */
const PLAY_COLUMN_ENTITY_IDS: ReadonlySet<string> = new Set(['LiveTrack', 'RawDdexTrack']);

/** True when the given search entity should render a Play column. */
export function searchEntityHasPlayColumn(entityId: string): boolean {
  return PLAY_COLUMN_ENTITY_IDS.has(entityId);
}

/**
 * Custom columns to prepend to the model-derived columns for a given search entity.
 * LiveTrack and RawDdexTrack each get a single Play column; on live tracks, holding
 * Option over that button streams HLS instead of the MP3 (no separate HLS column).
 * The custom list search gets one hidden column per schedule field.
 */
export function getSearchTableCustomColumns(entityId: string): TableColumnStaticProps[] {
  if (entityId === 'LocationZonePlayer') {
    return [CURRENTLY_PLAYING_COLUMN];
  }
  if (entityId === 'LocationZoneCustomPlaylist') {
    return customListScheduleTableColumns;
  }
  return searchEntityHasPlayColumn(entityId) ? [PLAY_COLUMN] : [];
}

/**
 * The columns the Track Matching page prepends to its LiveTrack-derived columns.
 *
 * These live HERE rather than in TrackMatchingPage because saved views need the
 * same list: `entityColumnUniverse` builds the universe a view is pruned against,
 * and anything the two lists disagree about is silently dropped from every saved
 * view. That has now bitten twice — once for the whole page, once for these
 * columns — so there is one definition and both sides read it.
 */
export const TRACK_MATCHING_CUSTOM_COLUMNS: TableColumnStaticProps[] = [
  {
    id: 'actions',
    key: 'actions',
    field: 'actions',
    header: 'Actions',
    dataType: 'display',
    isSortable: false,
    isFilterable: false,
    isUnique: false,
    isFilled: false,
    isProhibited: false,
  },
  {
    id: 'play',
    key: 'play',
    field: 'play',
    header: 'Play',
    dataType: 'display',
    isSortable: false,
    isFilterable: false,
    isUnique: false,
    isFilled: false,
    isProhibited: false,
    // Single fixed width — the Play button is a fixed-size icon; the column never resizes.
    fixedWidth: MIN_COLUMN_WIDTH_PX,
  },
  {
    id: 'history',
    key: 'history',
    field: 'history',
    header: 'History',
    dataType: 'display',
    isSortable: false,
    isFilterable: false,
    isUnique: false,
    isFilled: false,
    isProhibited: false,
    // Single fixed width — the History button is a fixed-size icon, but the
    // header text "History" (7 chars) needs more than MIN_COLUMN_WIDTH_PX (60px,
    // sized for "Play") or it truncates to "Histo…". 92px fits the label +
    // the sort-icon slot the header layout reserves with room to spare.
    fixedWidth: 80,
  },
  {
    id: 'source',
    key: 'source',
    field: 'source',
    header: 'Source',
    dataType: 'display',
    isSortable: false,
    isFilterable: false,
    isUnique: false,
    isFilled: false,
    isProhibited: false,
  },
  {
    id: 'live_track_id',
    key: 'live_track_id',
    field: 'live_track_id',
    header: 'Live Track ID',
    dataType: 'display',
    isSortable: false,
    isFilterable: false,
    isUnique: false,
    isFilled: false,
    isProhibited: false,
  },
  {
    id: 'upc',
    key: 'album.upc',
    field: 'album.upc',
    header: 'UPC',
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isUnique: false,
    isFilled: false,
    isProhibited: false,
  },
];
